import React from 'react';
import {MenuItem} from '../types';
import {useMerchant} from '../context/MerchantContext';
import ProductImage from './common/ProductImage';

interface MenuItemCardProps {
  item: MenuItem;
  stockControlEnabled?: boolean;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, stockControlEnabled }) => {
  const { setProductModalData, restaurant } = useMerchant();

  const isAvailable = item.available &&
    item.status !== 'UNAVAILABLE' &&
    (restaurant?.stockControlEnabled !== true || !item.inventory?.stockControl || item.inventory.stockQuantity > 0 || !item.inventory.blockWithoutStock);

  const handleCardClick = () => {
    if (!isAvailable) return;
    setProductModalData({ item, editMode: false } as any);
  };

  const hasPromotion = item.comercial?.promotionActive && (item.comercial.promotionalPrice || 0) < item.price;
  const currentPrice = hasPromotion ? item.comercial?.promotionalPrice : item.price;
  const hasPaidExtras = item.extras?.some(extra => extra.options.some(option => (option.price || 0) > 0)) || false;
  const isCombo = item.productType === 'COMBO';
  const comboSummary = isCombo
    ? item.subProducts?.slice(0, 3).map(subProduct => subProduct.name?.trim()).filter(Boolean).join(' + ') || 'Combo com itens selecionados'
    : '';

  const formattedPrice = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(currentPrice || 0);

  const formattedOriginalPrice = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(item.price);

  const comboAccent = restaurant?.theme?.primary || '#2563eb';
  const comboAccentStrong = `${comboAccent}2E`;

  return (
    <div
      className={`position-relative h-100 ${!isAvailable ? 'grayscale opacity-75' : 'cursor-pointer'}`}
      style={{
        minHeight: 'clamp(120px, 18vw, 160px)',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        cursor: isAvailable ? 'pointer' : 'default',
        overflow: 'visible'
      }}
      onClick={isAvailable ? handleCardClick : undefined}
    >
      <div
        className={`card border-0 shadow-sm rounded-4 overflow-hidden position-relative h-100 transition-all ${!isAvailable ? 'grayscale opacity-75' : 'hover-translate-up cursor-pointer'}`}
        style={{
          minHeight: 'clamp(120px, 18vw, 160px)',
          transition: 'all 0.3s ease',
          cursor: isAvailable ? 'pointer' : 'default',
          border: isCombo ? `1px solid ${comboAccentStrong}` : undefined,
          boxShadow: isCombo ? `0 14px 30px rgba(15, 23, 42, 0.08)` : undefined,
          background: isCombo ? `linear-gradient(180deg, ${comboAccent}08 0%, rgba(255,255,255,0.96) 22%)` : undefined
        }}
      >

        <div className="card-body p-0 d-flex flex-row h-100">
          <div className="p-2 p-md-3 d-flex flex-column flex-grow-1 min-w-0">
            <div className="flex-grow-1">
              <div className="d-flex gap-1 gap-md-2 mb-1 flex-wrap" style={{ marginTop: 0 }}>
                {item.featured && (
                  <span className="badge bg-warning bg-opacity-25 text-warning-emphasis fw-bold text-uppercase" style={{ fontSize: 'clamp(0.45rem, 1.2vw, 0.6rem)', padding: '1px 5px' }}>
                    MAIS PEDIDO
                  </span>
                )}
                {item.isNew && (
                  <span className="badge bg-success bg-opacity-25 text-success-emphasis fw-bold text-uppercase" style={{ fontSize: 'clamp(0.45rem, 1.2vw, 0.6rem)', padding: '1px 5px' }}>
                    NOVIDADE
                  </span>
                )}
              </div>

              <h3 className="h6 fw-bold text-dark mb-1 text-truncate-2" style={{ lineHeight: '1.1', fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)' }}>
                {item.name}
              </h3>

              {isCombo && (
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <span
                    className="badge rounded-pill fw-semibold text-uppercase d-inline-flex align-items-center gap-1"
                    style={{
                      fontSize: '0.62rem',
                      letterSpacing: '0.11em',
                      padding: '0.35rem 0.6rem',
                      background: `linear-gradient(135deg, ${comboAccent}15 0%, ${comboAccent}0A 100%)`,
                      color: comboAccent,
                      border: `1px solid ${comboAccent}26`
                    }}
                    title="Item do tipo combo"
                  >
                    <i className="fas fa-layer-group" style={{ fontSize: '0.66rem' }} />
                    Combo
                  </span>
                  <span
                    className="text-muted text-truncate"
                    style={{
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                      maxWidth: '100%'
                    }}
                    title={comboSummary}
                  >
                    {comboSummary}
                  </span>
                </div>
              )}

              <div
                className="small text-muted mb-1 text-truncate-2 text-md-truncate-3 lh-sm"
                style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)' }}
              >
                <div dangerouslySetInnerHTML={{ __html: item.description || 'Sem descrição disponível.' }} />
              </div>
            </div>

            <div className="mt-auto d-flex align-items-end gap-1 gap-md-2 flex-wrap">
              <div className="d-flex flex-column" style={{ gap: hasPaidExtras ? '0.1rem' : 0, lineHeight: 1 }}>
                {hasPaidExtras && (
                  <span className="small text-muted fw-semibold" style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)', color: '#4b5563' }}>
                    A partir de
                  </span>
                )}
                <span className="fw-bolder text-dark" style={{ fontSize: 'clamp(0.9rem, 2.8vw, 1.15rem)' }}>
                  {formattedPrice}
                </span>
              </div>
              {hasPromotion && (
                <span className="text-muted text-decoration-line-through small" style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)' }}>
                  {formattedOriginalPrice}
                </span>
              )}
            </div>
          </div>

          <div
            className="m-2 rounded-3 overflow-hidden position-relative flex-shrink-0"
            style={{ width: 'clamp(70px, 22vw, 110px)', height: 'clamp(70px, 22vw, 110px)' }}
          >
            <ProductImage
              src={item.image}
              alt={item.name}
              className="w-100 h-100 object-fit-cover"
            />
            {(item.originalPrice || hasPromotion || item.comercial?.highlight) && (
              <div
                className="position-absolute top-0 end-0 m-1 bg-danger text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm border border-2 border-white"
                style={{ width: 'clamp(20px, 5vw, 24px)', height: 'clamp(20px, 5vw, 24px)', fontSize: '0.7rem' }}
                title="Promoção"
              >
                <i className="fas fa-tag" />
              </div>
            )}
            {!isAvailable && (
              <div
                className="position-absolute top-50 start-50 translate-middle bg-secondary text-white fw-bold text-uppercase py-1 px-2 shadow-sm rounded-1 text-center"
                style={{
                  fontSize: '0.65rem',
                  zIndex: 10,
                  width: '80%',
                  pointerEvents: 'none',
                  border: '1.5px solid white'
                }}
              >
                Esgotado
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemCard;
